import React, { useState } from 'react';
import { addMember, updateMemberDetails } from '../firebase/database';
import { isMembershipPaused } from '../utils/membershipFees';
import './MembershipForm.css';

const EMPTY_FORM = {
  fullName: '',
  fullAddress: '',
  email: '',
  phone: '',
  dateOfBirth: {
    day: '',
    month: '',
    year: ''
  },
  age: '',
  sex: '',
  height: '',
  weight: '',
  fees: '',
  fitnessGoals: {
    fatReduction: false,
    energyEndurance: false,
    muscleStrength: false,
    timelessYouth: false,
    digestiveHealth: false,
    sculptedPhysique: false,
    challengeMuscles: false,
    sweatFit: false,
    allOfAbove: false
  },
  paymentMethod: 'cash',
  membershipDuration: 'monthly',
  membershipLevel: 'student',
  termsAccepted: false,
  signature: '',
  registrationDate: ''
};

// Stored as "DD/MM/YYYY" - split it back into the three inputs
const splitDateOfBirth = (dateOfBirth) => {
  if (!dateOfBirth) return { ...EMPTY_FORM.dateOfBirth };
  if (typeof dateOfBirth === 'object') {
    return { ...EMPTY_FORM.dateOfBirth, ...dateOfBirth };
  }

  const [day = '', month = '', year = ''] = String(dateOfBirth).split('/');
  return { day, month, year };
};

// <input type="date"> needs YYYY-MM-DD
const toDateInputValue = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);

  const date = new Date(value);
  if (isNaN(date.getTime())) return '';

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const buildInitialFormData = (member) => {
  if (!member) return { ...EMPTY_FORM };

  return {
    ...EMPTY_FORM,
    ...member,
    dateOfBirth: splitDateOfBirth(member.dateOfBirth),
    fitnessGoals: { ...EMPTY_FORM.fitnessGoals, ...(member.fitnessGoals || {}) },
    age: member.age ? String(member.age) : '',
    fees: member.fees ? String(member.fees) : '',
    height: member.height ? String(member.height) : '',
    weight: member.weight ? String(member.weight) : '',
    signature: member.signature || '',
    termsAccepted: Boolean(member.termsAccepted),
    registrationDate: toDateInputValue(member.registrationDate)
  };
};

function MembershipForm({ tenantId, member = null, onClose, onSuccess }) {
  const isEditMode = Boolean(member?.id);
  const [formData, setFormData] = useState(() => buildInitialFormData(member));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Function to calculate age from date of birth
  const calculateAge = (day, month, year) => {
    if (!day || !month || !year) {
      return '';
    }

    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    // Validate date values
    if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum)) {
      return '';
    }

    // Validate month (1-12)
    if (monthNum < 1 || monthNum > 12) {
      return '';
    }

    // Validate day (1-31, basic check)
    if (dayNum < 1 || dayNum > 31) {
      return '';
    }

    // Validate year (reasonable range)
    if (yearNum < 1900 || yearNum > new Date().getFullYear()) {
      return '';
    }

    try {
      const birthDate = new Date(yearNum, monthNum - 1, dayNum);
      const today = new Date();
      
      // Check if date is valid
      if (birthDate.getDate() !== dayNum || birthDate.getMonth() !== monthNum - 1 || birthDate.getFullYear() !== yearNum) {
        return '';
      }

      // Check if birth date is in the future
      if (birthDate > today) {
        return '';
      }

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age > 0 ? age.toString() : '';
    } catch (error) {
      return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => {
        const updated = {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: type === 'checkbox' ? checked : value
          }
        };

        // Auto-calculate age when date of birth changes
        if (parent === 'dateOfBirth') {
          const dob = updated.dateOfBirth;
          const calculatedAge = calculateAge(dob.day, dob.month, dob.year);
          updated.age = calculatedAge;
        }

        return updated;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.fullAddress.trim()) {
      newErrors.fullAddress = 'Full address is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.dateOfBirth.day || !formData.dateOfBirth.month || !formData.dateOfBirth.year) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      // Validate that the date is valid and age can be calculated
      const calculatedAge = calculateAge(
        formData.dateOfBirth.day,
        formData.dateOfBirth.month,
        formData.dateOfBirth.year
      );
      if (!calculatedAge) {
        newErrors.dateOfBirth = 'Please enter a valid date of birth';
      }
    }

    // Age is auto-calculated, so we don't require it to be manually entered
    // But we should validate that it was calculated successfully
    if (!formData.age.trim()) {
      // Only show error if date of birth is provided but age couldn't be calculated
      if (formData.dateOfBirth.day && formData.dateOfBirth.month && formData.dateOfBirth.year) {
        newErrors.dateOfBirth = 'Please enter a valid date of birth';
      }
    }

    if (!formData.sex.trim()) {
      newErrors.sex = 'Sex is required';
    }

    if (!formData.height.trim()) {
      newErrors.height = 'Height is required';
    }

    if (!formData.weight.trim()) {
      newErrors.weight = 'Weight is required';
    }

    if (!formData.fees.trim()) {
      newErrors.fees = 'Fees amount is required';
    }

    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the terms and conditions';
    }

    if (!formData.signature.trim()) {
      newErrors.signature = 'Signature is required';
    }

    if (!formData.registrationDate) {
      newErrors.registrationDate = 'Registration date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const memberData = {
        ...formData,
        dateOfBirth: `${formData.dateOfBirth.day}/${formData.dateOfBirth.month}/${formData.dateOfBirth.year}`,
        registrationDate: formData.registrationDate
      };

      let result;

      if (isEditMode) {
        // Editing is allowed in any membership state; updateMemberDetails keeps
        // the pause/resume and billing fields untouched.
        result = await updateMemberDetails(tenantId, member.id, memberData);
      } else {
        result = await addMember(tenantId, {
          ...memberData,
          status: 'active',
          membershipId: `MF${Date.now()}${Math.random().toString(36).substr(2, 5).toUpperCase()}`
        });
      }

      if (result.success) {
        console.log(isEditMode ? 'Member updated:' : 'Member added with ID:', result.memberId || member?.id);
        alert(isEditMode ? 'Member details updated successfully!' : 'Member registration successful!');

        // Close form and refresh the caller
        onSuccess && onSuccess();
        onClose();
      } else {
        console.error(isEditMode ? 'Error updating member:' : 'Error adding member:', result.message);
        alert(result.message);
      }

    } catch (error) {
      console.error('Unexpected error:', error);
      alert(isEditMode ? 'Error updating member. Please try again.' : 'Error registering member. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="membership-form-overlay">
      <div className="membership-form-container">
        <div className="form-header">
          <h1>{isEditMode ? 'EDIT MEMBER DETAILS' : 'FITNESS MEMBERSHIP FORM'}</h1>
          <div className="gym-branding">
            <h2>DE MUSCLE FACTORY</h2>
            <p>UNISEX GYM</p>
          </div>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <form onSubmit={handleSubmit} className="membership-form">
          <p className="form-instructions">
            {isEditMode
              ? `Update the details for ${member.fullName || 'this member'}. Membership status, pause history and payments are not affected.`
              : 'Please complete the form below to sign up for membership.'}
          </p>

          {isEditMode && isMembershipPaused(member) && (
            <div className="edit-paused-notice">
              ⏸️ This membership is currently <strong>paused</strong>. Details can still be edited —
              fees stay stopped until the membership is resumed.
            </div>
          )}

          {/* Terms & Conditions */}
          <div className="terms-section">
            <h3>☑️ Term & Conditions</h3>
            <ul>
              <li>Any student responsible for damaging equipment or property is liable for the associated costs.</li>
              <li>The Gym Administration or coaches bear no responsibility for accidents, injuries, or loss of life during practice sessions.</li>
              <li>Avoid monopolizing machines.</li>
              <li>Please return equipment to its designated place after use.</li>
              <li>Refrain from placing gym bags on the floor.</li>
              <li>Practice constructive feedback and avoid criticizing others.</li>
            </ul>
          </div>

          {/* Personal Information */}
          <div className="form-section">
            <h3>Personal Information</h3>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Full Name:</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={errors.fullName ? 'error' : ''}
                />
                {errors.fullName && <span className="error-message">{errors.fullName}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Full Address:</label>
                <input
                  type="text"
                  name="fullAddress"
                  value={formData.fullAddress}
                  onChange={handleInputChange}
                  className={errors.fullAddress ? 'error' : ''}
                />
                {errors.fullAddress && <span className="error-message">{errors.fullAddress}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>E-Mail:</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label>Phone:</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date Of Birth:</label>
                <div className="date-inputs">
                  <input
                    type="text"
                    name="dateOfBirth.day"
                    placeholder="DD"
                    maxLength="2"
                    value={formData.dateOfBirth.day}
                    onChange={handleInputChange}
                    className={errors.dateOfBirth ? 'error' : ''}
                  />
                  <input
                    type="text"
                    name="dateOfBirth.month"
                    placeholder="MM"
                    maxLength="2"
                    value={formData.dateOfBirth.month}
                    onChange={handleInputChange}
                    className={errors.dateOfBirth ? 'error' : ''}
                  />
                  <input
                    type="text"
                    name="dateOfBirth.year"
                    placeholder="YYYY"
                    maxLength="4"
                    value={formData.dateOfBirth.year}
                    onChange={handleInputChange}
                    className={errors.dateOfBirth ? 'error' : ''}
                  />
                </div>
                {errors.dateOfBirth && <span className="error-message">{errors.dateOfBirth}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Age:</label>
                <input
                  type="text"
                  name="age"
                  value={formData.age}
                  readOnly
                  placeholder="Auto-calculated"
                  className={errors.age ? 'error' : ''}
                  style={{
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed',
                    color: formData.age ? '#333' : '#999'
                  }}
                />
                {errors.age && <span className="error-message">{errors.age}</span>}
              </div>
              <div className="form-group">
                <label>Sex:</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="sex"
                      value="male"
                      checked={formData.sex === 'male'}
                      onChange={handleInputChange}
                    />
                    <span>Male</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="sex"
                      value="female"
                      checked={formData.sex === 'female'}
                      onChange={handleInputChange}
                    />
                    <span>Female</span>
                  </label>
                </div>
                {errors.sex && <span className="error-message">{errors.sex}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Height:</label>
                <input
                  type="text"
                  name="height"
                  placeholder="e.g., 5'8&quot; or 172 cm"
                  value={formData.height}
                  onChange={handleInputChange}
                  className={errors.height ? 'error' : ''}
                />
                {errors.height && <span className="error-message">{errors.height}</span>}
              </div>
              <div className="form-group">
                <label>Weight:</label>
                <input
                  type="text"
                  name="weight"
                  placeholder="e.g., 70 kg or 154 lbs"
                  value={formData.weight}
                  onChange={handleInputChange}
                  className={errors.weight ? 'error' : ''}
                />
                {errors.weight && <span className="error-message">{errors.weight}</span>}
              </div>
            </div>


          </div>

          {/* Fitness Goals */}
          <div className="fitness-goals-section">
            <h3>Which of the following are important to you?</h3>
            <div className="goals-grid">
              <div className="goals-column">
                <label className="goal-checkbox">
                  <input
                    type="checkbox"
                    name="fitnessGoals.fatReduction"
                    checked={formData.fitnessGoals.fatReduction}
                    onChange={handleInputChange}
                  />
                  <span>Fat Reduction and Weight Loss Strategies for Health</span>
                </label>
                <label className="goal-checkbox">
                  <input
                    type="checkbox"
                    name="fitnessGoals.energyEndurance"
                    checked={formData.fitnessGoals.energyEndurance}
                    onChange={handleInputChange}
                  />
                  <span>Energy & Endurance</span>
                </label>
                <label className="goal-checkbox">
                  <input
                    type="checkbox"
                    name="fitnessGoals.muscleStrength"
                    checked={formData.fitnessGoals.muscleStrength}
                    onChange={handleInputChange}
                  />
                  <span>Enhance muscle strength while maintaining a sleek and formidable physique.</span>
                </label>
                <label className="goal-checkbox">
                  <input
                    type="checkbox"
                    name="fitnessGoals.timelessYouth"
                    checked={formData.fitnessGoals.timelessYouth}
                    onChange={handleInputChange}
                  />
                  <span>"Endless energy, timeless youth, and a radiant spirit."</span>
                </label>
                <label className="goal-checkbox">
                  <input
                    type="checkbox"
                    name="fitnessGoals.digestiveHealth"
                    checked={formData.fitnessGoals.digestiveHealth}
                    onChange={handleInputChange}
                  />
                  <span>A Healthier Digestive System</span>
                </label>
                <label className="goal-checkbox">
                  <input
                    type="checkbox"
                    name="fitnessGoals.sculptedPhysique"
                    checked={formData.fitnessGoals.sculptedPhysique}
                    onChange={handleInputChange}
                  />
                  <span>Sculpted physique</span>
                </label>
              </div>
              <div className="goals-column">
                <label className="goal-checkbox">
                  <input
                    type="checkbox"
                    name="fitnessGoals.challengeMuscles"
                    checked={formData.fitnessGoals.challengeMuscles}
                    onChange={handleInputChange}
                  />
                  <span>Challenge your muscles!</span>
                </label>
                <label className="goal-checkbox">
                  <input
                    type="checkbox"
                    name="fitnessGoals.sweatFit"
                    checked={formData.fitnessGoals.sweatFit}
                    onChange={handleInputChange}
                  />
                  <span>Sweat it out & Get Fit</span>
                </label>
                <label className="goal-checkbox">
                  <input
                    type="checkbox"
                    name="fitnessGoals.allOfAbove"
                    checked={formData.fitnessGoals.allOfAbove}
                    onChange={handleInputChange}
                  />
                  <span>All Of The Above</span>
                </label>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="form-section">
            <h3>Payment Method:</h3>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={formData.paymentMethod === 'cash'}
                  onChange={handleInputChange}
                />
                <span>Cash</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="transfer"
                  checked={formData.paymentMethod === 'transfer'}
                  onChange={handleInputChange}
                />
                <span>Transfer</span>
              </label>
            </div>
          </div>

          {/* Membership Duration */}
          <div className="form-section">
            <h3>Membership Level (Duration):</h3>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="membershipDuration"
                  value="annual"
                  checked={formData.membershipDuration === 'annual'}
                  onChange={handleInputChange}
                />
                <span>Annual</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="membershipDuration"
                  value="3months"
                  checked={formData.membershipDuration === '3months'}
                  onChange={handleInputChange}
                />
                <span>3 Months</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="membershipDuration"
                  value="6months"
                  checked={formData.membershipDuration === '6months'}
                  onChange={handleInputChange}
                />
                <span>6 Months</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="membershipDuration"
                  value="monthly"
                  checked={formData.membershipDuration === 'monthly'}
                  onChange={handleInputChange}
                />
                <span>Monthly</span>
              </label>
            </div>
          </div>

          {/* Membership Level */}
          <div className="form-section">
            <h3>Membership Level</h3>
            <div className="membership-levels">
              <div className="level-group">
                <h4>Membership Level (A)</h4>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="membershipLevel"
                    value="student"
                    checked={formData.membershipLevel === 'student'}
                    onChange={handleInputChange}
                  />
                  <span>Student Membership</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="membershipLevel"
                    value="adult"
                    checked={formData.membershipLevel === 'adult'}
                    onChange={handleInputChange}
                  />
                  <span>Adult Membership</span>
                </label>
              </div>
              <div className="level-group">
                <h4>Membership Level (B)</h4>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="membershipLevel"
                    value="individual"
                    checked={formData.membershipLevel === 'individual'}
                    onChange={handleInputChange}
                  />
                  <span>Individual Membership</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="membershipLevel"
                    value="old"
                    checked={formData.membershipLevel === 'old'}
                    onChange={handleInputChange}
                  />
                  <span>Old Membership</span>
                </label>
              </div>
            </div>
          </div>

          {/* Fees */}
          <div className="fees-section">
            <h3>Membership Fees</h3>
            <div className="fees-input">
              <label>Amount:</label>
              <input
                type="text"
                name="fees"
                placeholder="e.g., ₹2000 or $50"
                value={formData.fees}
                onChange={handleInputChange}
                className={errors.fees ? 'error' : ''}
              />
            </div>
            {errors.fees && <span className="error-message">{errors.fees}</span>}
            <div className="fees-info">
              <p>💰 Enter the membership fee amount. You can use any currency format (₹, $, €, etc.) and include the amount with or without currency symbol.</p>
            </div>
          </div>

          {/* Registration Date */}
          <div className="registration-date-section">
            <h3>Registration Date</h3>
            <div className="registration-date-input">
              <label>Select Date:</label>
              <input
                type="date"
                name="registrationDate"
                value={formData.registrationDate}
                onChange={handleInputChange}
                className={errors.registrationDate ? 'error' : ''}
                required
              />
            </div>
            {errors.registrationDate && <span className="error-message">{errors.registrationDate}</span>}
            <div className="registration-date-info">
              <p>📅 Choose the date when this member will be registered. This can be today's date or a future date for advance registrations.</p>
            </div>
          </div>

          {/* Signature */}
          <div className="form-section">
            <label>Student's Signature:</label>
            <input
              type="text"
              name="signature"
              value={formData.signature}
              onChange={handleInputChange}
              placeholder="Type your full name as signature"
              className={errors.signature ? 'error' : ''}
            />
            {errors.signature && <span className="error-message">{errors.signature}</span>}
          </div>

          {/* Terms Acceptance */}
          <div className="form-section">
            <label className="checkbox-option">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleInputChange}
              />
              <span>I accept the terms and conditions</span>
            </label>
            {errors.termsAccepted && <span className="error-message">{errors.termsAccepted}</span>}
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting
                ? (isEditMode ? 'Saving...' : 'Submitting...')
                : (isEditMode ? 'Save Changes' : 'Submit Membership Form')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MembershipForm; 